import { useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Inbox as InboxIcon, MessageSquare, ChevronRight } from 'lucide-react';

const InboxTab = ({ inbox, navigate, i18n, markSeen, refreshInbox }) => {
  // Auto-mark as seen when the user opens this tab (with unread items)
  useEffect(() => {
    if (inbox?.unread_count > 0) {
      markSeen();
    }
    // eslint-disable-next-line
  }, []);

  const convs = inbox?.conversations || [];

  if (convs.length === 0) {
    return (
      <div className="max-w-7xl mx-auto mb-8">
        <Card className="bg-[#0d3d3d]/60 border-white/10">
          <CardContent className="py-12 text-center text-white/60">
            <InboxIcon className="w-12 h-12 mx-auto mb-3 text-white/30" />
            <p className="text-sm font-medium">{i18n.language === 'nl' ? 'Geen berichten' : 'No messages'}</p>
            <p className="text-xs mt-1">
              {i18n.language === 'nl'
                ? 'Heeft u vragen? Open een offerte en stel uw vraag — wij antwoorden binnen 1 werkdag.'
                : 'Have questions? Open a quote and ask — we reply within 1 business day.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso.slice(0, 10);
    }
  };

  return (
    <div className="max-w-7xl mx-auto mb-8" data-testid="client-inbox-section">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
        <InboxIcon className="w-6 h-6 text-[#3da5a5]" />
        {i18n.language === 'nl' ? 'Inbox' : 'Inbox'} ({convs.length})
      </h2>
      <div className="grid gap-3">
        {convs.map((conv) => {
          const last = conv.last_comment || {};
          const isFromAdmin = last.author_type === 'admin';
          return (
            <Card
              key={conv.quote_id}
              className={`bg-[#0d3d3d]/90 border-white/20 hover:border-[#3da5a5]/60 transition-all cursor-pointer ${conv.unread > 0 ? 'ring-2 ring-[#3da5a5]/60' : ''}`}
              onClick={() => navigate(`/offerte/${conv.public_token}?from=portal`)}
              data-testid={`client-inbox-conv-${conv.quote_id}`}
            >
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`mt-1 ${isFromAdmin ? 'text-[#3da5a5]' : 'text-white/60'}`}>
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <p className="font-semibold text-white">
                      {i18n.language === 'nl' ? 'Offerte' : 'Quote'} {conv.quote_number}
                      {conv.unread > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center text-[10px] font-bold bg-[#3da5a5] text-white rounded-full px-1.5 min-w-[18px]">
                          {conv.unread} {i18n.language === 'nl' ? 'nieuw' : 'new'}
                        </span>
                      )}
                    </p>
                    <span className="text-[11px] text-white/40">{fmtDate(conv.last_at)}</span>
                  </div>
                  <p className="text-xs text-white/70 mt-1">
                    <strong className={isFromAdmin ? 'text-[#3da5a5]' : 'text-white'}>
                      {isFromAdmin ? (last.author_name + ' · Cardoso Bouw') : (last.author_name || (i18n.language === 'nl' ? 'U' : 'You'))}:
                    </strong>{' '}
                    <span className="line-clamp-2">{last.body}</span>
                  </p>
                  <p className="text-[10px] text-white/40 mt-1">{conv.total_comments} {i18n.language === 'nl' ? 'berichten in deze conversatie' : 'messages in this thread'}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-white/40" />
              </CardContent>
            </Card>
          );
        })}
      </div>
      <p className="text-[11px] text-white/40 mt-4 text-center">
        {i18n.language === 'nl'
          ? '💡 Klik op een conversatie om de offerte te openen en te antwoorden.'
          : '💡 Click a conversation to open the quote and reply.'}
      </p>
    </div>
  );
};

export default InboxTab;
